// pages/support/knowledge/KnowledgeListPage.tsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Edit, Trash2, Eye, Search, X, ChevronLeft, ChevronRight } from "lucide-react";
import PageListLayout from "../../../../layouts/PageListLayout";
import useKnowledge from "../../../../hooks/support/knowledge/useKnowledge";
import { KnowledgeArticle } from "../../../../types/features/support/KnowledgeArticle";
import DeleteConfirmationModal from "../../../../components/ui/DeleteConfirmationModal";
import { alertSuccess, alertError } from "../../../../helpers/alertError";

export default function KnowledgeListPage() {
  const navigate = useNavigate();
  const { articles, getAllArticles, deleteArticle, loading, pagination } = useKnowledge();
  const [searchTerm, setSearchTerm] = useState("");
  const [articleToDelete, setArticleToDelete] = useState<KnowledgeArticle | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 20;

  useEffect(() => {
    getAllArticles(currentPage, perPage);
  }, [currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    getAllArticles(page, perPage);
  };

  const filteredArticles = articles.filter((article) => {
    const search = searchTerm.toLowerCase();
    return (
      article.title.toLowerCase().includes(search) ||
      article.category.toLowerCase().includes(search) ||
      article.keywords?.some((k) => k.toLowerCase().includes(search))
    );
  });

  const handleDelete = async () => {
    if (!articleToDelete) return;
    setIsDeleting(true);
    const success = await deleteArticle(articleToDelete.id);
    setIsDeleting(false);
    if (success) {
      setArticleToDelete(null);
      alertSuccess("Article supprimé avec succès");
      getAllArticles(currentPage, perPage);
    } else {
      alertError("Erreur lors de la suppression");
    }
  };

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case "Problème":
        return { label: "Problème", className: "bg-red-100 text-red-700" };
      case "Procédure Agent":
        return { label: "Procédure agent", className: "bg-blue-100 text-blue-700" };
      case "Procédure Technique":
        return { label: "Procédure technique", className: "bg-purple-100 text-purple-700" };
      case "Escalade":
        return { label: "Escalade", className: "bg-orange-100 text-orange-700" };
      case "Réponse Client":
        return { label: "Réponse client", className: "bg-green-100 text-green-700" };
      default:
        return { label: category || "Article", className: "bg-gray-100 text-gray-700" };
    }
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">Actif</span>
    ) : (
      <span className="text-xs px-2 py-1 bg-gray-100 text-gray-500 rounded-full">Inactif</span>
    );
  };

  return (
    <PageListLayout
      title="Gestion de la base de connaissances"
      actions={
        <button
          onClick={() => navigate("/admin/knowledge/create")}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700"
        >
          <Plus className="w-4 h-4" />
          Nouvel article
        </button>
      }
    >
      <div className="p-6">
        {/* Recherche */}
        <div className="relative max-w-md mb-6">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher un article..."
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Tableau */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          </div>
        ) : filteredArticles.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Aucun article trouvé</p>
            <button
              onClick={() => navigate("/support/knowledge/admin/create")}
              className="mt-4 text-green-600 hover:text-green-700"
            >
              Créer le premier article
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Titre</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Catégorie</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-600">Mots-clés</th>
                    <th className="px-6 py-3 text-center text-sm font-medium text-gray-600">Statut</th>
                    <th className="px-6 py-3 text-center text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredArticles.map((article) => {
                    const category = getCategoryLabel(article.category);
                    return (
                      <tr key={article.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-gray-900">{article.title}</p>
                            <p className="text-xs text-gray-500 line-clamp-1">{article.description}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`text-xs px-2 py-1 rounded-full ${category.className}`}>
                            {category.label}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1">
                            {article.keywords?.slice(0, 3).map((keyword, idx) => (
                              <span key={idx} className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                                {keyword}
                              </span>
                            ))}
                            {article.keywords && article.keywords.length > 3 && (
                              <span className="text-xs text-gray-400">+{article.keywords.length - 3}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-center">{getStatusBadge(article.is_active)}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => navigate(`/support/knowledge/admin/edit/${article.id}`)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Modifier"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setArticleToDelete(article)}
                              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {/* Pagination */}
            {pagination.total > 0 && (
              <div className="flex items-center justify-between mt-6">
                <span className="text-sm text-gray-600">
                  Page {pagination.currentPage} sur {pagination.lastPage} ({pagination.total} articles)
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Précédent
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === pagination.lastPage}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Suivant
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de confirmation de suppression */}
      <DeleteConfirmationModal
        isOpen={!!articleToDelete}
        onClose={() => setArticleToDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer l'article"
        message={`Supprimer l'article "${articleToDelete?.title}" ? Cette action est irréversible.`}
        confirmText={isDeleting ? "Suppression..." : "Supprimer"}
      />
    </PageListLayout>
  );
}
